// The contents (the binder): an ARIA tree with a roving focus. Up/Down move, Right opens a folder or goes into it,
// Left closes it or goes to its parent, Home/End jump, Enter or Space opens the item on the current page.
//
// Editing (W4), as the Mac's binder and Structure menu: ⌘/Ctrl-click and ⇧-click select several; drag rows before,
// after or into a folder (a line or a wash shows where); ⌥⌘↑/↓ move among siblings, ⌘] into the item above, ⌘[ out of
// the folder; F2 renames in place (Return keeps it, Escape leaves it); Delete asks, then moves to the Trash; the
// context menu (or ⇧F10) has the rest. Each is one edit of the project, through `structureEdits`. In a collection
// (read-only) the menu has Status and Label only. With the revision heatmap on, rows are tinted as the cards are.
//
// Rows as the sidebar redesign draws them (`design-docs/Sidebar redesign — design.md`): a folder has its chevron, its
// chapter or part number (as compile numbers it), its title and how many documents are under it ("3 of 5" while
// filtering); a document has a tile in its label's colour with its kind's icon, its title and its words.
//
// With the filter on (`filtered`: store/binderFilter.ts) the tree given is the matches and the folders above them: those
// folders open themselves (until closed by hand, while the filter stays the same), and the words found show under each
// matching row; a row edited out of the filter while selected stays, faint. Drawing what the filter leaves out faded
// (`dims`) gives the whole tree: documents left out at 30%, folders with nothing matching at 50%. The menu adds Filter
// By and Focus.
import { memo, useContext, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import type { OpenedProject, TreeNode } from '@writing-suite/core';
import { revisionHeat } from '@writing-suite/core/revise';
import { PREF, useSharedPreference } from '../store/plan.ts';
import { heatOfNodes, type RowFacts } from '../store/planModel.ts';
import { cssColor, formatNumber, hasChildren, pathTo, readingFragments } from '../views/helpers.ts';
import { Tile, type IconName } from '../views/Icons.tsx';
import { PresenceDots } from '../views/Presence.tsx';
import { planItemMenu } from '../views/plan/menus.ts';
import { MenuPopup } from '../views/Menu.tsx';
import { filterMenuEntries, SnippetLine } from '../views/FilterBar.tsx';
import { folderCounts, folderDetail, type FilterView } from '../store/binderFilter.ts';
import { dropPlacement, dropZone, isFolderish, keyMovePlacement, rangeBetween, type DropZone, type KeyMove } from '../views/structure.ts';
import { structureEdits, WorkspaceRefContext } from './workspace.ts';

interface Row {
  node: TreeNode;
  depth: number;
  parent: string | null;
}

function visibleRows(nodes: TreeNode[], isOpen: (id: string) => boolean, depth = 0, parent: string | null = null, out: Row[] = []): Row[] {
  for (const node of nodes) {
    out.push({ node, depth, parent });
    if (node.children.length && isOpen(node.id)) visibleRows(node.children, isOpen, depth + 1, node.id, out);
  }
  return out;
}

const DRAG_TYPE = 'application/x-writing-suite-nodes';
/** The documents being dragged, as the corkboard gives them: what a mind map makes cards of. */
const FRAGMENTS_TYPE = 'application/x-writing-suite-fragments';

/** A document's tile icon, by its kind. A map shows as a note until the canvas comes (maps phase 2). */
const KIND_ICONS: Record<string, IconName> = { prose: 'doc', screenplay: 'script', note: 'note', research: 'research', 'front-matter': 'book', map: 'note' };
const NO_FACTS: RowFacts = new Map();

/** Memoized: typing rebuilds the project, not the contents, so the tree re-renders only when its own props change (it
 *  reads the workspace as it is now through a stable reference). */
export const ContentsTree = memo(function ContentsTree({
  tree,
  selected,
  hrefFor,
  onChoose,
  label,
  editable,
  locked,
  numbers,
  renaming,
  filtered = null,
  fullTree,
  dims = false,
  facts = NO_FACTS,
  inManuscript = false,
}: {
  tree: TreeNode[];
  selected: string | null;
  hrefFor: (node: TreeNode) => string;
  onChoose: (node: TreeNode) => void;
  label: string;
  /** Structure can be edited here (`canStructure`). */
  editable: boolean;
  /** Fragments under lock: shown with a lock after the title. */
  locked?: ReadonlySet<string>;
  /** Chapter and part numbers by fragment id ("14", "II"), as compile numbers them; shown before the title. */
  numbers?: ReadonlyMap<string, string>;
  /** The item being renamed in place. */
  renaming: string | null;
  /** The filter's view of the arrangement, when one is on. */
  filtered?: FilterView | null;
  /** The arrangement's whole tree (a folder's count is of everything under it). */
  fullTree?: TreeNode[];
  /** The tree given is the whole tree, and what the filter leaves out is drawn faded. */
  dims?: boolean;
  /** Each document's words, label colour and kind, by fragment id. */
  facts?: RowFacts;
  /** The manuscript: an item left out of the compile is drawn faint, as compile and the chapter count pass it by. */
  inManuscript?: boolean;
}) {
  const wsRef = useContext(WorkspaceRefContext);
  const ws = wsRef.current;
  // Top-level folders start open, as do the selection's ancestors.
  const [open, setOpen] = useState<Set<string>>(() => new Set(tree.filter((n) => n.children.length).map((n) => n.id)));
  useEffect(() => {
    if (!selected) return;
    const path = pathTo(tree, selected);
    if (path && path.slice(0, -1).some((n) => !open.has(n.id))) setOpen((o) => new Set([...o, ...path.slice(0, -1).map((n) => n.id)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, tree]);

  // Folders the filter opened that the writer closed (until the filter changes).
  const [closedByHand, setClosedByHand] = useState<Set<string>>(new Set());
  const filterKey = filtered?.key ?? null;
  useEffect(() => setClosedByHand(new Set()), [filterKey]);
  const isOpen = (id: string) => (filtered?.result.opened.has(id) ? !closedByHand.has(id) : open.has(id));
  const rows = useMemo(() => visibleRows(tree, isOpen), [tree, open, filtered, closedByHand]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => folderCounts(fullTree ?? tree, filtered, (id) => facts.get(id)?.words ?? 0), [fullTree, tree, filtered, facts]);
  // The revision heatmap tints the rows as it does the cards (a folder takes its busiest scene's colour).
  const [heatOn] = useSharedPreference(PREF.heat, false);
  const [heatDays] = useSharedPreference(PREF.heatDays, 30);
  const heat = useMemo(() => {
    const p = ws?.project as OpenedProject | undefined;
    return heatOn && p && typeof p.fragmentRecord === 'function' ? heatOfNodes(tree, revisionHeat(p, heatDays)) : {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatOn, heatDays, tree]);
  const [focus, setFocus] = useState<string | null>(null);
  const focusId = rows.some((r) => r.node.id === focus) ? focus : rows.some((r) => r.node.id === selected) ? selected : (rows[0]?.node.id ?? null);
  const list = useRef<HTMLUListElement>(null);
  const moveFocus = (id: string) => {
    setFocus(id);
    requestAnimationFrame(() => list.current?.querySelector<HTMLElement>(`[data-node-id="${CSS.escape(id)}"]`)?.focus());
  };

  // The selection: the page's item, plus any ⌘- or ⇧-clicked; the anchor is where a ⇧-click's range starts.
  const [picked, setPicked] = useState<string[]>(selected ? [selected] : []);
  const anchor = useRef<string | null>(selected);
  useEffect(() => {
    setPicked(selected ? [selected] : []);
    anchor.current = selected;
  }, [selected]);
  const pickedNow = picked.filter((id) => rows.some((r) => r.node.id === id) || !!pathTo(tree, id));
  /** What an action on a row acts on: the selection if the row is in it, else the row. */
  const targets = (id: string) => (pickedNow.includes(id) ? rows.map((r) => r.node.id).filter((x) => pickedNow.includes(x)) : [id]);

  // A row moved by the keyboard (or a drop) keeps the focus once the tree has it in its new place.
  const refocus = useRef<string | null>(null);
  useEffect(() => {
    const id = refocus.current;
    if (!id || !rows.some((r) => r.node.id === id)) return;
    refocus.current = null;
    moveFocus(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const toggle = (id: string, to?: boolean) => {
    if (filtered?.result.opened.has(id)) {
      const closed = closedByHand.has(id);
      if (to === undefined || to === closed) setClosedByHand((c) => { const next = new Set(c); if (closed) next.delete(id); else next.add(id); return next; });
      return;
    }
    setOpen((o) => {
      const next = new Set(o);
      if (to ?? !next.has(id)) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // The structure edits over the workspace as it is when they're made.
  const edits = {
    move: (...a: Parameters<ReturnType<typeof structureEdits>['move']>) => (wsRef.current ? structureEdits(wsRef.current).move(...a) : undefined),
    rename: (...a: Parameters<ReturnType<typeof structureEdits>['rename']>) => (wsRef.current ? structureEdits(wsRef.current).rename(...a) : undefined),
  };
  const [menu, setMenu] = useState<{ x: number; y: number; ids: string[]; from: string } | null>(null);

  const keyMove = (row: Row, direction: KeyMove) => {
    if (!editable || !edits) return;
    const placement = keyMovePlacement(tree, row.node.id, direction);
    if (!placement) return;
    refocus.current = row.node.id;
    if (direction === 'into' && 'inside' in placement && placement.inside) setOpen((o) => new Set([...o, placement.inside!]));
    edits.move([row.node.id], placement);
  };

  const onKey = (e: KeyboardEvent, row: Row, index: number) => {
    if (e.target !== e.currentTarget) return; // the rename field's own keys
    const mod = e.metaKey || e.ctrlKey;
    if (editable && mod && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      keyMove(row, e.key === 'ArrowUp' ? 'up' : 'down');
      e.preventDefault();
      return;
    }
    // (⌃⌘] and ⌃⌘[ are Next and Previous Status: views/plan/menus.ts.)
    if (editable && mod && !e.altKey && !(e.ctrlKey && e.metaKey) && (e.key === ']' || e.key === '[' || e.code === 'BracketRight' || e.code === 'BracketLeft')) {
      keyMove(row, e.key === ']' || e.code === 'BracketRight' ? 'into' : 'outOf');
      e.preventDefault();
      return;
    }
    if (e.key === 'F2' && editable && row.node.fragmentId) {
      wsRef.current?.setRenaming(row.node.id);
      e.preventDefault();
      return;
    }
    if ((e.key === 'Delete' || (e.key === 'Backspace' && mod)) && editable) {
      wsRef.current?.askTrash(targets(row.node.id));
      e.preventDefault();
      return;
    }
    if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setMenu({ x: r.left + 24, y: r.bottom, ids: targets(row.node.id), from: row.node.id });
      e.preventDefault();
      return;
    }
    if (e.altKey || mod) return;
    const folder = row.node.children.length > 0;
    switch (e.key) {
      case 'ArrowDown':
        if (rows[index + 1]) moveFocus(rows[index + 1].node.id);
        break;
      case 'ArrowUp':
        if (rows[index - 1]) moveFocus(rows[index - 1].node.id);
        break;
      case 'Home':
        if (rows[0]) moveFocus(rows[0].node.id);
        break;
      case 'End':
        if (rows.length) moveFocus(rows[rows.length - 1].node.id);
        break;
      case 'ArrowRight':
        if (folder && !isOpen(row.node.id)) toggle(row.node.id, true);
        else if (folder) moveFocus(row.node.children[0].id);
        break;
      case 'ArrowLeft':
        if (folder && isOpen(row.node.id)) toggle(row.node.id, false);
        else if (row.parent) moveFocus(row.parent);
        break;
      case 'Enter':
      case ' ':
        onChoose(row.node);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  // Drag and drop: the rows being moved, and where they'd land.
  const dragging = useRef<string[] | null>(null);
  const [over, setOver] = useState<{ id: string; zone: DropZone } | null>(null);
  const zoneOf = (e: DragEvent, node: TreeNode): DropZone => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return dropZone((e.clientY - r.top) / Math.max(1, r.height), isFolderish(node));
  };
  const onDragOver = (e: DragEvent, node: TreeNode) => {
    const moving = dragging.current;
    if (!moving || !editable) return;
    const zone = zoneOf(e, node);
    if (!dropPlacement(tree, moving, node.id, zone)) {
      if (over) setOver(null);
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (over?.id !== node.id || over.zone !== zone) setOver({ id: node.id, zone });
  };
  const onDrop = (e: DragEvent, node: TreeNode) => {
    const moving = dragging.current;
    setOver(null);
    dragging.current = null;
    if (!moving || !edits) return;
    e.preventDefault();
    const zone = zoneOf(e, node);
    const placement = dropPlacement(tree, moving, node.id, zone);
    if (!placement) return;
    if (zone === 'into') setOpen((o) => new Set([...o, node.id]));
    edits.move(moving, placement);
  };

  const choose = (e: React.MouseEvent, row: Row) => {
    if (e.button !== 0 || e.altKey) return;
    const id = row.node.id;
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
      anchor.current = id;
      return;
    }
    if (e.shiftKey) {
      e.preventDefault();
      setPicked(rangeBetween(rows.map((r) => r.node.id), anchor.current ?? selected ?? id, id));
      return;
    }
    e.preventDefault();
    anchor.current = id;
    onChoose(row.node);
  };

  return (
    <>
      <ul className="contents-tree" role="tree" aria-label={label} aria-multiselectable={editable || undefined} ref={list}>
        {rows.map((row, index) => {
          const folder = row.node.children.length > 0;
          const folderish = folder || row.node.isFolder;
          const isPicked = pickedNow.includes(row.node.id);
          const isCurrent = row.node.id === selected;
          const drop = over?.id === row.node.id ? ` drop-${over.zone}` : '';
          // Where the row stands in the filter: a match, a folder above one, the selection kept after an edit, or left out.
          const standing = !filtered ? null
            : filtered.result.matches.has(row.node.id) ? 'match'
            : filtered.kept === row.node.id ? 'kept'
            : filtered.result.context.has(row.node.id) ? 'context' : 'out';
          const snip = filtered?.query && row.node.fragmentId && filtered.result.matches.has(row.node.id) ? ws?.filter?.snippet(row.node.fragmentId) : null;
          const count = folderish ? counts.get(row.node.id) : undefined;
          const fact = !folderish && row.node.fragmentId ? facts.get(row.node.fragmentId) : undefined;
          const excluded = inManuscript && !!row.node.fragmentId && facts.get(row.node.fragmentId)?.included === false;
          const number = row.node.fragmentId ? numbers?.get(row.node.fragmentId) : undefined;
          return (
            <li
              key={row.node.id}
              role="treeitem"
              aria-level={row.depth + 1}
              aria-selected={isPicked}
              aria-current={isCurrent ? 'page' : undefined}
              aria-expanded={folder ? isOpen(row.node.id) : undefined}
              aria-description={standing === 'kept' ? 'Edited out of the filter' : standing === 'out' && dims ? 'Not in the filter' : undefined}
              tabIndex={row.node.id === focusId ? 0 : -1}
              data-node-id={row.node.id}
              className={`tree-row${isCurrent ? ' selected' : ''}${isPicked && !isCurrent ? ' picked' : ''}${hasChildren(row.node) || folderish ? ' folder' : ' document'}${drop}${heat[row.node.id] ? ` heat heat-${heat[row.node.id]}` : ''}${standing === 'kept' ? ' filter-kept' : ''}${standing === 'out' ? ' filter-out' : ''}${excluded ? ' excluded' : ''}${snip ? ' with-snippet' : ''}`}
              data-heat={heat[row.node.id] || undefined}
              data-filter={standing ?? undefined}
              ref={(el) => el?.style.setProperty('--depth', String(row.depth))}
              draggable={editable && renaming !== row.node.id}
              onKeyDown={(e) => onKey(e, row, index)}
              onFocus={() => setFocus(row.node.id)}
              onContextMenu={(e) => {
                if (!ws) return;
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, ids: targets(row.node.id), from: row.node.id });
              }}
              onDragStart={(e) => {
                const ids = targets(row.node.id);
                dragging.current = ids;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(ids));
                // The documents themselves, for a mind map (a card each) and the beat board.
                const fragments = rows.filter((r) => ids.includes(r.node.id)).flatMap((r) => (r.node.fragmentId ? [r.node.fragmentId] : []));
                if (fragments.length) e.dataTransfer.setData(FRAGMENTS_TYPE, JSON.stringify(fragments));
                e.dataTransfer.setData('text/plain', row.node.title || 'Untitled');
              }}
              onDragEnd={() => {
                dragging.current = null;
                setOver(null);
              }}
              onDragOver={(e) => onDragOver(e, row.node)}
              onDragLeave={(e) => {
                if (over?.id === row.node.id && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null);
              }}
              onDrop={(e) => onDrop(e, row.node)}
            >
              {folder ? (
                <button
                  type="button"
                  className="disclosure"
                  tabIndex={-1}
                  aria-hidden="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(row.node.id);
                  }}
                >
                  <span className={isOpen(row.node.id) ? 'chevron open' : 'chevron'} />
                </button>
              ) : (
                <span className="disclosure-space" />
              )}
              {renaming === row.node.id ? (
                <RenameField
                  title={row.node.title}
                  onDone={(title, how) => {
                    if (title !== null && edits) edits.rename(row.node.id, title);
                    wsRef.current?.setRenaming(null);
                    if (how === 'enter' && isCurrent) wsRef.current?.focusEditor();
                    else moveFocus(row.node.id);
                  }}
                />
              ) : (
                <>
                  {folderish ? (
                    <span className={`tree-number${number ? '' : ' none'}`} aria-hidden="true">{number ?? ''}</span>
                  ) : (
                    <Tile icon={KIND_ICONS[fact?.kind ?? 'prose'] ?? 'doc'} color={cssColor(fact?.label)} size={20} className="tree-tile" />
                  )}
                  <a href={hrefFor(row.node)} tabIndex={-1} draggable={false} onClick={(e) => choose(e, row)} onDoubleClick={() => editable && row.node.fragmentId && wsRef.current?.setRenaming(row.node.id)}>
                    {row.node.title || 'Untitled'}
                  </a>
                </>
              )}
              {row.node.fragmentId && locked?.has(row.node.fragmentId) ? <span className="tree-lock" role="img" aria-label="Locked">🔒</span> : null}
              <PresenceDots fragmentIds={readingFragments([row.node])} />
              {count ? (
                <span className="tree-count" aria-hidden="true">{folderDetail(count, !!filtered) ?? ''}</span>
              ) : fact ? (
                <span className="tree-words" aria-hidden="true">{formatNumber(fact.words)}</span>
              ) : null}
              {snip ? <SnippetLine snippet={snip} /> : null}
            </li>
          );
        })}
      </ul>
      {menu && ws ? (
        <MenuPopup
          label="Item"
          at={menu}
          entries={[
            ...planItemMenu(ws, menu.ids, {
              onRename: (id) => ws.setRenaming(id),
              onCreated: (id) => {
                ws.open(id);
                ws.setRenaming(id);
              },
            }),
            ...(menu.ids.length === 1 ? filterMenuEntries(ws, menu.ids[0]) : []),
          ]}
          onClose={(restore) => {
            const from = menu.from;
            setMenu(null);
            if (restore) moveFocus(from);
          }}
        />
      ) : null}
    </>
  );
});

/** A title edited in place: Return keeps it, Escape leaves it as it was, leaving the field keeps it. */
export function RenameField({ title, onDone, className = 'rename-field' }: { title: string; onDone: (title: string | null, how: 'enter' | 'escape' | 'blur') => void; className?: string }) {
  const [draft, setDraft] = useState(title || 'Untitled');
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (value: string | null, how: 'enter' | 'escape' | 'blur') => {
    if (done.current) return;
    done.current = true;
    onDone(value, how);
  };
  return (
    <input
      ref={ref}
      className={className}
      aria-label="Title"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(draft, 'enter');
        } else if (e.key === 'Escape') {
          e.preventDefault();
          finish(null, 'escape');
        }
      }}
      onBlur={() => finish(draft, 'blur')}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
